import {useAuiState} from '@assistant-ui/react';
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown';
import Markdown from '@wandb/weave/common/components/Markdown';
import {Link} from '@wandb/weave/common/util/links';
import {MarkdownContainer} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/ChatView/MarkdownContainer';
import React, {memo} from 'react';
import remarkGfm from 'remark-gfm';

import {CodeSnippet} from '../CodeSnippet';
import {sanitizeHref} from './sanitizeHref';

// keeping this for userMessage, but it will eventually be replaced by AUI's
// DirectiveText when we switch the composer/mention system over.
export const WBAgentMarkdown = memo(({content}: {content: string}) => (
  <MarkdownContainer>
    <Markdown content={content} shouldEjectFromTailwind={false} />
  </MarkdownContainer>
));
WBAgentMarkdown.displayName = 'WBAgentMarkdown';

export const Anchor = ({
  href,
  children,
}: {
  href?: string;
  children?: React.ReactNode;
}) => {
  const sanitizedHref = sanitizeHref(href);
  if (!sanitizedHref) {
    return <>{children}</>;
  }

  return (
    <Link
      to={sanitizedHref}
      className="font-medium text-teal-600 underline hover:text-teal-500">
      {children}
    </Link>
  );
};

const Code = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => {
  const isCodeBlock = useIsMarkdownCodeBlock();
  if (isCodeBlock) {
    const language = className?.replace(/^language-/, '') ?? 'python';
    return (
      <div className="pb-16">
        <CodeSnippet
          codeSnippet={String(children).replace(/\n$/, '')}
          codeLanguage={language}
        />
      </div>
    );
  }
  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

const assistantMarkdownComponents = memoizeMarkdownComponents({
  a: Anchor,
  pre: ({children}) => <>{children}</>,
  code: Code,
});

export const MarkdownText = memo(() => {
  const hasText = useAuiState(
    s => s.part.type === 'text' && s.part.text.length > 0
  );
  // Have MarkdownText render null for an empty text part
  // to fix extra space between a ToolCall disclosure row
  // and the DotProgress wave loader. The trailing empty Text
  // component is added by AUI's MessagePrimitive.Parts if
  // the last real part is not text or reasoning
  if (!hasText) {
    return null;
  }
  return (
    <div className="mt-12 first:mt-0">
      <MarkdownContainer>
        <MarkdownTextPrimitive
          remarkPlugins={[remarkGfm]}
          components={assistantMarkdownComponents}
        />
      </MarkdownContainer>
    </div>
  );
});

MarkdownText.displayName = 'MarkdownText';
